import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Download, Edit, Trash2, Calendar, CheckCircle, 
  FileText, Mail, Receipt, Tag, Upload, File, Users, Building2, 
  Save, X, Loader2, Printer, Search, UserPlus, User, Briefcase, AlertTriangle,
  Shield, Key, Send
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { expedientesAPI, contactosAPI, participantesAPI } from '../api';
import { toast } from '../hooks/use-toast';
import AdjuntosSection from '../components/AdjuntosSection';
import ActuacionesSection from '../components/ActuacionesSection';
import TareasSection from '../components/TareasSection';
import NotasSection from '../components/NotasSection';
import EventosSection from '../components/EventosSection';
import FloatingActionButton from '../components/FloatingActionButton';
import PagosSection from '../components/PagosSection';
import AnalisisIAExpediente from '../components/AnalisisIAExpediente';

// Roles disponibles para participantes
const ROLES_PARTICIPANTE = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'potencial_cliente', label: 'Potencial cliente' },
  { value: 'abogado_propio', label: 'Abogado propio' },
  { value: 'abogado_colaborador', label: 'Abogado colaborador' },
  { value: 'abogado_contrario', label: 'Abogado contrario' },
  { value: 'procurador_propio', label: 'Procurador propio' },
  { value: 'procurador_contrario', label: 'Procurador contrario' },
  { value: 'parte_contraria', label: 'Parte contraria' },
  { value: 'perito', label: 'Perito' },
  { value: 'testigo_propio', label: 'Testigo propio' },
  { value: 'testigo_parte_contraria', label: 'Testigo de la parte contraria' },
  { value: 'colaborador', label: 'Colaborador' },
  { value: 'asesor', label: 'Asesor' },
  { value: 'gestor', label: 'Gestor' },
  { value: 'intermediario', label: 'Intermediario' },
  { value: 'otorgante', label: 'Otorgante' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'otro', label: 'Otro' }
];

const ExpedienteDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [expediente, setExpediente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Diálogos
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isContactoDialogOpen, setIsContactoDialogOpen] = useState(false);
  const [isOrganismoDialogOpen, setIsOrganismoDialogOpen] = useState(false);
  const [isActuacionDialogOpen, setIsActuacionDialogOpen] = useState(false);
  const [isTareaDialogOpen, setIsTareaDialogOpen] = useState(false);
  const [isNotaDialogOpen, setIsNotaDialogOpen] = useState(false);
  
  // Estados de edición
  const [editForm, setEditForm] = useState({});
  const [contactoForm, setContactoForm] = useState({
    tipo: 'cliente',
    nombre: '',
    nif_cif: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    codigo_postal: '',
    provincia: '',
    notas: ''
  });
  const [organismoForm, setOrganismoForm] = useState({
    nombre: '',
    tipo_organismo: 'juzgado',
    direccion: '',
    telefono: '',
    email: '',
    localidad: '',
    provincia: ''
  });
  
  // Estados locales para secciones
  const [actuaciones, setActuaciones] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [notas, setNotas] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [eventos, setEventos] = useState([]);
  
  // Referencias para secciones (para scroll del FAB)
  const actuacionesRef = React.useRef(null);
  const tareasRef = React.useRef(null);
  const notasRef = React.useRef(null);
  const eventosRef = React.useRef(null);
  
  // Participantes
  const [participantes, setParticipantes] = useState([]);
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactoForm, setNewContactoForm] = useState({
    nombre: '',
    nif_cif: '',
    email: '',
    telefono: '',
    direccion: '',
    tipo: 'cliente'
  });
  const [contactosDisponibles, setContactosDisponibles] = useState([]);
  const [organismosDisponibles, setOrganismosDisponibles] = useState([]);
  const [searchContacto, setSearchContacto] = useState('');
  const [selectedContacto, setSelectedContacto] = useState(null);
  const [selectedRol, setSelectedRol] = useState('cliente');
  const [tipoParticipante, setTipoParticipante] = useState('contacto');
  const [loadingContactos, setLoadingContactos] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [editTab, setEditTab] = useState('datos');

  useEffect(() => {
    loadExpediente();
    loadParticipantes();
  }, [id]);

  // Abrir diálogo de edición si viene con ?edit=true
  useEffect(() => {
    if (searchParams.get('edit') === 'true' && expediente) {
      setIsEditDialogOpen(true);
      // Limpiar el parámetro de la URL
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [expediente, searchParams, setSearchParams]);

  const loadExpediente = async () => {
    try {
      setLoading(true);
      const response = await expedientesAPI.getById(id);
      const exp = response.data;
      setExpediente(exp);
      setEditForm({
        numero_expediente: exp.numero_expediente || '',
        expediente_judicial: exp.expediente_judicial || '',
        nig: exp.nig || '',
        referencia_extrajudicial: exp.referencia_extrajudicial || '',
        titulo: exp.titulo || '',
        serie: exp.serie || '',
        codigo: exp.codigo || '',
        descripcion: exp.descripcion || '',
        tipo: exp.tipo || '',
        tipo_procedimiento: exp.tipo_procedimiento || '',
        rama: exp.rama || '',
        clase: exp.clase || '',
        fase: exp.fase || '',
        estado: exp.estado || 'activo',
        juzgado: exp.juzgado || '',
        juzgado_id: exp.juzgado_id || null,
        procurador: exp.procurador || '',
        contrario: exp.contrario || '',
        cliente_id: exp.cliente_id || '',
        valor_economico: exp.valor_economico || 0,
        fecha_inicio: exp.fecha_inicio?.split('T')[0] || '',
        observaciones: exp.observaciones || ''
      });
      
      // Cargar actuaciones por separado
      try {
        const actResponse = await expedientesAPI.getActuaciones(id);
        // Mapear descripcion a observaciones y añadir titulo desde tipo si no existe
        const mappedActuaciones = actResponse.data.map(act => ({
          ...act,
          titulo: act.titulo || act.tipo || 'Actuación',
          observaciones: act.descripcion || act.observaciones || ''
        }));
        setActuaciones(mappedActuaciones);
      } catch (err) {
        console.log('No se pudieron cargar actuaciones:', err);
        setActuaciones([]);
      }
      
      setTareas(exp.tareas || []);
      setNotas(exp.notas || []);
      setFacturas(exp.facturas || []);
    } catch (error) {
      console.error('Error cargando expediente:', error);
      toast({ title: "Error", description: "No se pudo cargar el expediente", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadParticipantes = async () => {
    try {
      const response = await participantesAPI.getByExpediente(id);
      setParticipantes(response.data);
    } catch (error) {
      console.error('Error cargando participantes:', error);
    }
  };

  const loadContactosDisponibles = async (searchTerm = '') => {
    setLoadingContactos(true);
    try {
      // Buscar contactos con el término de búsqueda
      const params = { 
        page_size: 50,
        search: searchTerm || undefined
      };
      const response = await contactosAPI.getAll(params);
      // La API devuelve { data: [...], pagination: {...} }
      const todos = response.data?.data || response.data || [];
      const contactos = todos.filter(c => c.tipo !== 'organismo');
      const organismos = todos.filter(c => c.tipo === 'organismo');
      setContactosDisponibles(contactos);
      setOrganismosDisponibles(organismos);
    } catch (error) {
      console.error('Error cargando contactos:', error);
    } finally {
      setLoadingContactos(false);
    }
  };

  // Buscar contactos cuando el usuario escribe (con debounce)
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (searchContacto.length >= 2) {
      const timeout = setTimeout(() => {
        loadContactosDisponibles(searchContacto);
      }, 300);
      setSearchTimeout(timeout);
    } else if (searchContacto.length === 0) {
      // Si no hay búsqueda, cargar los primeros 50
      loadContactosDisponibles();
    }
    
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchContacto]);

  // Los contactos ya vienen filtrados de la API
  const filteredContactos = contactosDisponibles;

  const filteredOrganismos = organismosDisponibles;

  // Agrupar participantes por rol
  const participantesPorRol = participantes.reduce((acc, p) => {
    const rol = p.rol_display || p.rol;
    if (!acc[rol]) acc[rol] = [];
    acc[rol].push(p);
    return acc;
  }, {});

  const handleAddParticipante = async () => {
    if (!selectedContacto) {
      toast({ title: "Error", description: "Selecciona un contacto o organismo", variant: "destructive" });
      return;
    }
    
    setSaving(true);
    try {
      await participantesAPI.add({
        expediente_id: parseInt(id),
        contacto_id: tipoParticipante === 'contacto' ? selectedContacto.id : null,
        organismo_id: tipoParticipante === 'organismo' ? selectedContacto.id : null,
        tipo_participante: tipoParticipante,
        rol: selectedRol
      });
      
      toast({ title: "Participante añadido", description: `${selectedContacto.nombre} se ha añadido como ${ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label}` });
      setSelectedContacto(null);
      setSearchContacto('');
      loadParticipantes();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo añadir el participante", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveParticipante = async (participanteId) => {
    try {
      await participantesAPI.remove(participanteId);
      toast({ title: "Participante eliminado" });
      loadParticipantes();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar el participante", variant: "destructive" });
    }
  };

  const handleEditExpediente = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Transform data to handle empty strings for integer fields
      const transformedData = {
        ...editForm,
        cliente_id: editForm.cliente_id === '' ? null : editForm.cliente_id,
        juzgado_id: editForm.juzgado_id === '' ? null : editForm.juzgado_id,
        valor_economico: editForm.valor_economico === '' ? 0 : parseFloat(editForm.valor_economico) || 0
      };
      
      await expedientesAPI.update(id, transformedData);
      toast({ title: "Expediente actualizado", description: "Los cambios se han guardado correctamente" });
      setIsEditDialogOpen(false);
      loadExpediente();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo actualizar el expediente", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateContacto = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await contactosAPI.create({
        nombre: contactoForm.nombre,
        tipo: contactoForm.tipo,
        nif_cif: contactoForm.nif_cif,
        email: contactoForm.email,
        telefono: contactoForm.telefono,
        direccion: contactoForm.direccion,
        ciudad: contactoForm.ciudad,
        codigo_postal: contactoForm.codigo_postal,
        provincia: contactoForm.provincia,
        notas: contactoForm.notas
      });
      toast({ title: "Contacto creado", description: `${contactoForm.nombre} se ha añadido a la base de datos` });
      setIsContactoDialogOpen(false);
      setContactoForm({ tipo: 'cliente', nombre: '', nif_cif: '', email: '', telefono: '', direccion: '', ciudad: '', codigo_postal: '', provincia: '', notas: '' });
      loadContactosDisponibles();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear el contacto", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOrganismo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await contactosAPI.create({
        ...organismoForm,
        tipo: 'organismo',
        nombre: organismoForm.nombre,
        expediente_id: id
      });
      toast({ title: "Organismo creado", description: `${organismoForm.nombre} se ha añadido a la base de datos` });
      setIsOrganismoDialogOpen(false);
      setOrganismoForm({ nombre: '', tipo_organismo: 'juzgado', direccion: '', telefono: '', email: '', localidad: '', provincia: '' });
      loadContactosDisponibles();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo crear el organismo", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Crear contacto nuevo y añadirlo como participante
  const handleCreateContactoAndAddParticipante = async () => {
    if (!newContactoForm.nombre.trim()) {
      toast({ title: "Error", description: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    if (!selectedRol) {
      toast({ title: "Error", description: "Selecciona un rol para el participante", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      // 1. Crear el contacto
      const contactoResponse = await contactosAPI.create({
        nombre: newContactoForm.nombre,
        tipo: newContactoForm.tipo || 'cliente',
        nif_cif: newContactoForm.nif_cif || '',
        email: newContactoForm.email || '',
        telefono: newContactoForm.telefono || '',
        direccion: newContactoForm.direccion || ''
      });

      const nuevoContactoId = contactoResponse.data.id;

      // 2. Añadirlo como participante al expediente
      await participantesAPI.create({
        expediente_id: parseInt(id),
        contacto_id: nuevoContactoId,
        rol: selectedRol,
        tipo_participante: 'contacto'
      });

      toast({ 
        title: "Contacto creado y añadido", 
        description: `${newContactoForm.nombre} se ha creado y añadido como ${ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label}` 
      });

      // 3. Limpiar formulario y recargar datos
      setNewContactoForm({ nombre: '', nif_cif: '', email: '', telefono: '', direccion: '', tipo: 'cliente' });
      setShowNewContactForm(false);
      loadContactosDisponibles();
      loadParticipantes();
    } catch (error) {
      console.error('Error creando contacto:', error);
      toast({ title: "Error", description: error.response?.data?.detail || "No se pudo crear el contacto", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadReport = async () => {
    toast({ title: "Generando informe", description: "El documento Word se está preparando..." });
    
    try {
      // Usar el endpoint del backend para generar un documento Word real
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expedientes/${id}/documento`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al generar el documento');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Expediente_${expediente?.numero_expediente || id}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: "Informe descargado", description: "El documento Word se ha generado correctamente" });
    } catch (error) {
      console.error('Error descargando documento:', error);
      toast({ title: "Error", description: "No se pudo generar el informe", variant: "destructive" });
    }
  };

  const handleCreateActuacion = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newActuacion = {
      id: String(actuaciones.length + 1),
      titulo: formData.get('titulo'),
      fecha: formData.get('fecha') || new Date().toISOString().split('T')[0],
      observaciones: formData.get('observaciones'),
      duracion: formData.get('duracion') || '00:00:00',
    };
    setActuaciones([...actuaciones, newActuacion]);
    setIsActuacionDialogOpen(false);
    toast({ title: 'Actuación creada' });
  };

  // Nueva función para crear actuación con API real
  const handleCreateActuacionNew = async (data) => {
    try {
      const response = await expedientesAPI.createActuacion(id, {
        titulo: data.titulo,
        fecha: data.fecha || new Date().toISOString().split('T')[0],
        tipo: 'general',
        descripcion: data.observaciones || '',
        duracion: data.duracion
      });
      toast({ title: 'Actuación creada' });
      return response.data; // Devuelve la actuación con su ID
    } catch (error) {
      console.error('Error creando actuación:', error);
      throw error;
    }
  };

  const handleCreateTarea = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newTarea = {
      id: String(tareas.length + 1),
      titulo: formData.get('titulo'),
      descripcion: formData.get('descripcion'),
      fechaFin: formData.get('fechaFin'),
      estado: formData.get('estado'),
      prioridad: formData.get('prioridad'),
    };
    setTareas([...tareas, newTarea]);
    setIsTareaDialogOpen(false);
    toast({ title: 'Tarea creada' });
  };

  const handleCreateNota = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newNota = {
      id: String(notas.length + 1),
      titulo: formData.get('titulo'),
      descripcion: formData.get('descripcion'),
      fecha: new Date().toISOString().split('T')[0],
      etiquetas: formData.get('etiquetas')?.split(',') || []
    };
    setNotas([...notas, newNota]);
    setIsNotaDialogOpen(false);
    toast({ title: 'Nota creada' });
  };

  // Handler del FAB - Abre el acordeón correspondiente y hace scroll
  const handleFABAction = (actionType) => {
    // Map de acciones a secciones del acordeón
    const sectionMap = {
      'actuacion': { ref: actuacionesRef, accordion: 'actuaciones' },
      'tarea': { ref: tareasRef, accordion: 'tareas' },
      'nota': { ref: notasRef, accordion: 'notas' },
      'evento': { ref: eventosRef, accordion: 'eventos' }
    };
    
    const section = sectionMap[actionType];
    if (section?.ref?.current) {
      // Scroll suave a la sección
      section.ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      // Simular click en el botón de nueva (después del scroll)
      setTimeout(() => {
        const newButton = section.ref.current.querySelector('[data-fab-trigger="true"]');
        if (newButton) {
          newButton.click();
        }
      }, 500);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!expediente) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Expediente no encontrado</p>
        <Button onClick={() => navigate('/expedientes')} className="mt-4">Volver a expedientes</Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/expedientes')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📁 {expediente.numero_expediente}</h1>
            <p className="text-lg text-gray-800 mt-1 font-medium">{expediente.titulo}</p>
            {expediente.descripcion && expediente.descripcion !== expediente.titulo && (
              <p className="text-gray-600 mt-1 text-sm">{expediente.descripcion}</p>
            )}
            {/* Mostrar cliente si existe */}
            {(() => {
              const cliente = participantes.find(p => p.rol === 'cliente');
              if (cliente) {
                return (
                  <p className="text-blue-600 mt-1 text-sm font-medium flex items-center gap-1">
                    <User className="w-3 h-3" /> Cliente: {cliente.nombre}
                  </p>
                );
              }
              return null;
            })()}
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex flex-wrap gap-2">
          {/* CREAR CONTACTO - Solo crea en la base de datos (mismo formulario que Contactos) */}
          <Dialog open={isContactoDialogOpen} onOpenChange={setIsContactoDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-blue-600 text-white hover:bg-blue-700">
                <Users className="w-4 h-4 mr-2" />CREAR CONTACTO
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" />Nuevo Contacto</DialogTitle>
                <DialogDescription>Añade un nuevo contacto a la base de datos del despacho</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateContacto}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Nombre completo *</Label>
                      <Input value={contactoForm.nombre} onChange={(e) => setContactoForm({...contactoForm, nombre: e.target.value})} required />
                    </div>
                    <div>
                      <Label>Tipo de contacto</Label>
                      <Select value={contactoForm.tipo} onValueChange={(v) => setContactoForm({...contactoForm, tipo: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cliente">Cliente</SelectItem>
                          <SelectItem value="contraparte">Contraparte</SelectItem>
                          <SelectItem value="procurador">Procurador</SelectItem>
                          <SelectItem value="perito">Perito</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>NIF/CIF</Label>
                      <Input value={contactoForm.nif_cif} onChange={(e) => setContactoForm({...contactoForm, nif_cif: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={contactoForm.email} onChange={(e) => setContactoForm({...contactoForm, email: e.target.value})} />
                    </div>
                    <div>
                      <Label>Teléfono</Label>
                      <Input value={contactoForm.telefono} onChange={(e) => setContactoForm({...contactoForm, telefono: e.target.value})} />
                    </div>
                    <div className="col-span-2">
                      <Label>Dirección</Label>
                      <Input value={contactoForm.direccion} onChange={(e) => setContactoForm({...contactoForm, direccion: e.target.value})} />
                    </div>
                    <div>
                      <Label>Ciudad</Label>
                      <Input value={contactoForm.ciudad} onChange={(e) => setContactoForm({...contactoForm, ciudad: e.target.value})} />
                    </div>
                    <div>
                      <Label>Código Postal</Label>
                      <Input value={contactoForm.codigo_postal} onChange={(e) => setContactoForm({...contactoForm, codigo_postal: e.target.value})} />
                    </div>
                    <div>
                      <Label>Provincia</Label>
                      <Input value={contactoForm.provincia} onChange={(e) => setContactoForm({...contactoForm, provincia: e.target.value})} />
                    </div>
                    <div className="col-span-2">
                      <Label>Notas</Label>
                      <Textarea value={contactoForm.notas} onChange={(e) => setContactoForm({...contactoForm, notas: e.target.value})} rows={3} />
                    </div>
                  </div>
                </div>
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setIsContactoDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Crear Contacto
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* CREAR ORGANISMO - Solo crea en la base de datos */}
          <Dialog open={isOrganismoDialogOpen} onOpenChange={setIsOrganismoDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-purple-600 text-white hover:bg-purple-700">
                <Building2 className="w-4 h-4 mr-2" />CREAR ORGANISMO
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" />Crear nuevo organismo público</DialogTitle>
                <DialogDescription>Añade un juzgado, ayuntamiento u otro organismo a la base de datos</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateOrganismo}>
                <div className="space-y-4">
                  <div>
                    <Label>Tipo de organismo *</Label>
                    <Select value={organismoForm.tipo_organismo} onValueChange={(v) => setOrganismoForm({...organismoForm, tipo_organismo: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="juzgado">Juzgado</SelectItem>
                        <SelectItem value="audiencia">Audiencia Provincial</SelectItem>
                        <SelectItem value="tribunal_supremo">Tribunal Supremo</SelectItem>
                        <SelectItem value="ayuntamiento">Ayuntamiento</SelectItem>
                        <SelectItem value="hacienda">Agencia Tributaria</SelectItem>
                        <SelectItem value="registro">Registro de la Propiedad/Mercantil</SelectItem>
                        <SelectItem value="notaria">Notaría</SelectItem>
                        <SelectItem value="otro">Otro organismo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Nombre del organismo *</Label><Input value={organismoForm.nombre} onChange={(e) => setOrganismoForm({...organismoForm, nombre: e.target.value})} required /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Localidad</Label><Input value={organismoForm.localidad} onChange={(e) => setOrganismoForm({...organismoForm, localidad: e.target.value})} /></div>
                    <div><Label>Provincia</Label><Input value={organismoForm.provincia} onChange={(e) => setOrganismoForm({...organismoForm, provincia: e.target.value})} /></div>
                  </div>
                  <div><Label>Dirección</Label><Textarea value={organismoForm.direccion} onChange={(e) => setOrganismoForm({...organismoForm, direccion: e.target.value})} rows={2} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Teléfono</Label><Input value={organismoForm.telefono} onChange={(e) => setOrganismoForm({...organismoForm, telefono: e.target.value})} /></div>
                    <div><Label>Email</Label><Input type="email" value={organismoForm.email} onChange={(e) => setOrganismoForm({...organismoForm, email: e.target.value})} /></div>
                  </div>
                </div>
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setIsOrganismoDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* EDITAR - Incluye gestión de participantes */}
          <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (open) { setEditTab('datos'); loadContactosDisponibles(); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-green-600 text-white hover:bg-green-700">
                <Edit className="w-4 h-4 mr-2" />EDITAR
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5" />Editar expediente {expediente.serie}-{expediente.codigo}</DialogTitle>
              </DialogHeader>
              
              <Tabs value={editTab} onValueChange={setEditTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="datos" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />Datos
                  </TabsTrigger>
                  <TabsTrigger value="participantes" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />Participantes
                  </TabsTrigger>
                  <TabsTrigger value="organismos" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />Organismos
                  </TabsTrigger>
                </TabsList>

                {/* TAB: DATOS DEL EXPEDIENTE */}
                <TabsContent value="datos" className="mt-4">
                  <form onSubmit={handleEditExpediente}>
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Datos básicos</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label>Nº Expediente</Label><Input value={editForm.numero_expediente} onChange={(e) => setEditForm({...editForm, numero_expediente: e.target.value})} placeholder="Ej: EXP13303" /></div>
                          <div><Label>Expediente Judicial</Label><Input value={editForm.expediente_judicial} onChange={(e) => setEditForm({...editForm, expediente_judicial: e.target.value})} placeholder="Ej: 123/2025" /></div>
                          <div><Label>NIG</Label><Input value={editForm.nig} onChange={(e) => setEditForm({...editForm, nig: e.target.value})} placeholder="Número Identificación General" /></div>
                          <div><Label>Nº Reclamación Extrajudicial</Label><Input value={editForm.referencia_extrajudicial} onChange={(e) => setEditForm({...editForm, referencia_extrajudicial: e.target.value})} placeholder="Ej: EB-2026-00001" /></div>
                        </div>
                        <div className="mt-4">
                          <Label>Título *</Label>
                          <Input value={editForm.titulo} onChange={(e) => setEditForm({...editForm, titulo: e.target.value})} required placeholder="Título del expediente" />
                        </div>
                        <div className="mt-4">
                          <Label>Descripción</Label>
                          <Textarea value={editForm.descripcion} onChange={(e) => setEditForm({...editForm, descripcion: e.target.value})} rows={3} placeholder="Descripción detallada del expediente (opcional)" />
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Tag className="w-4 h-4" /> Clasificación</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Rama</Label>
                            <Select value={editForm.rama || editForm.tipo} onValueChange={(v) => setEditForm({...editForm, rama: v, tipo: v})}>
                              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Civil">Civil</SelectItem>
                                <SelectItem value="Mercantil">Mercantil</SelectItem>
                                <SelectItem value="Laboral">Laboral</SelectItem>
                                <SelectItem value="Penal">Penal</SelectItem>
                                <SelectItem value="Administrativo">Administrativo</SelectItem>
                                <SelectItem value="Familia">Familia</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Tipo Procedimiento</Label>
                            <Select value={editForm.tipo_procedimiento || editForm.clase} onValueChange={(v) => setEditForm({...editForm, tipo_procedimiento: v, clase: v})}>
                              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Ordinario">Ordinario</SelectItem>
                                <SelectItem value="Verbal">Verbal</SelectItem>
                                <SelectItem value="Monitorio">Monitorio</SelectItem>
                                <SelectItem value="Ejecutivo">Ejecutivo</SelectItem>
                                <SelectItem value="General">General</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Estado</Label>
                            <Select value={editForm.estado} onValueChange={(v) => setEditForm({...editForm, estado: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="activo">Activo</SelectItem>
                                <SelectItem value="suspendido">Suspendido</SelectItem>
                                <SelectItem value="cerrado">Cerrado</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> Datos procesales</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Fase procesal</Label>
                            <Select value={editForm.fase} onValueChange={(v) => setEditForm({...editForm, fase: v})}>
                              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Inicio">Inicio</SelectItem>
                                <SelectItem value="Demanda">Demanda</SelectItem>
                                <SelectItem value="Contestación">Contestación</SelectItem>
                                <SelectItem value="Audiencia Previa">Audiencia Previa</SelectItem>
                                <SelectItem value="Pruebas">Pruebas</SelectItem>
                                <SelectItem value="Sentencia">Sentencia</SelectItem>
                                <SelectItem value="Recurso">Recurso</SelectItem>
                                <SelectItem value="Ejecución">Ejecución</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div><Label>Fecha inicio</Label><Input type="date" value={editForm.fecha_inicio} onChange={(e) => setEditForm({...editForm, fecha_inicio: e.target.value})} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div><Label>Contrario</Label><Input value={editForm.contrario} onChange={(e) => setEditForm({...editForm, contrario: e.target.value})} placeholder="Parte contraria" /></div>
                          <div><Label>Valor económico (€)</Label><Input type="number" value={editForm.valor_economico} onChange={(e) => setEditForm({...editForm, valor_economico: parseFloat(e.target.value) || 0})} /></div>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Observaciones</h3>
                        <Textarea value={editForm.observaciones} onChange={(e) => setEditForm({...editForm, observaciones: e.target.value})} rows={4} />
                      </div>
                    </div>
                    <DialogFooter className="mt-6">
                      <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar cambios
                      </Button>
                    </DialogFooter>
                  </form>
                </TabsContent>

                {/* TAB: PARTICIPANTES */}
                <TabsContent value="participantes" className="mt-4">
                  <div className="space-y-6">
                    {/* Añadir participante */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <UserPlus className="w-4 h-4" /> Añadir participante de la base de datos
                      </h3>
                      
                      {/* Tipo de participante */}
                      <div className="flex gap-2 mb-4">
                        <Button 
                          type="button"
                          variant={tipoParticipante === 'contacto' ? 'default' : 'outline'}
                          onClick={() => { setTipoParticipante('contacto'); setSelectedContacto(null); }}
                          className="flex-1"
                        >
                          <User className="w-4 h-4 mr-2" />Contactos
                        </Button>
                        <Button 
                          type="button"
                          variant={tipoParticipante === 'organismo' ? 'default' : 'outline'}
                          onClick={() => { setTipoParticipante('organismo'); setSelectedContacto(null); }}
                          className="flex-1"
                        >
                          <Building2 className="w-4 h-4 mr-2" />Organismos
                        </Button>
                      </div>

                      {/* Selector de rol */}
                      <div className="mb-4">
                        <Label>Rol del participante *</Label>
                        <Select value={selectedRol} onValueChange={setSelectedRol}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES_PARTICIPANTE.map(rol => (
                              <SelectItem key={rol.value} value={rol.value}>{rol.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Buscador */}
                      <div className="mb-4">
                        <Label>Buscar {tipoParticipante === 'contacto' ? 'contacto' : 'organismo'}</Label>
                        <div className="relative mt-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input 
                            value={searchContacto}
                            onChange={(e) => setSearchContacto(e.target.value)}
                            placeholder="Buscar por nombre, email o NIF..."
                            className="pl-10"
                          />
                        </div>
                      </div>

                      {/* Lista de resultados */}
                      <div className="border rounded-lg max-h-[200px] overflow-y-auto bg-white mb-4">
                        {loadingContactos ? (
                          <div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
                        ) : (tipoParticipante === 'contacto' ? filteredContactos : filteredOrganismos).length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <p>No se encontraron {tipoParticipante === 'contacto' ? 'contactos' : 'organismos'}</p>
                            {tipoParticipante === 'contacto' && !showNewContactForm && (
                              <Button 
                                type="button"
                                variant="link" 
                                className="mt-2 text-blue-600"
                                onClick={() => setShowNewContactForm(true)}
                              >
                                <Plus className="w-4 h-4 mr-1" /> Crear nuevo contacto
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="divide-y">
                            {(tipoParticipante === 'contacto' ? filteredContactos : filteredOrganismos).map((item) => (
                              <div 
                                key={item.id}
                                onClick={() => setSelectedContacto(item)}
                                className={`p-3 cursor-pointer hover:bg-blue-50 transition-colors ${selectedContacto?.id === item.id ? 'bg-blue-100 border-l-4 border-blue-600' : ''}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tipoParticipante === 'contacto' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                                    {tipoParticipante === 'contacto' ? <User className="w-4 h-4 text-blue-600" /> : <Building2 className="w-4 h-4 text-purple-600" />}
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      {item.nombre_completo || [item.nombre, item.primer_apellido, item.segundo_apellido].filter(Boolean).join(' ') || item.nombre}
                                    </p>
                                    <div className="flex gap-3 text-xs text-gray-500">
                                      {item.email && <span>✉️ {item.email}</span>}
                                      {item.telefono && <span>📞 {item.telefono}</span>}
                                    </div>
                                  </div>
                                  {selectedContacto?.id === item.id && <CheckCircle className="w-5 h-5 text-blue-600" />}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Contacto seleccionado */}
                      {selectedContacto && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                          <p className="text-sm text-blue-700">
                            <strong>Seleccionado:</strong> {selectedContacto.nombre_completo || [selectedContacto.nombre, selectedContacto.primer_apellido, selectedContacto.segundo_apellido].filter(Boolean).join(' ') || selectedContacto.nombre} como <strong>{ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label}</strong>
                          </p>
                        </div>
                      )}

                      {/* Formulario para crear nuevo contacto */}
                      {showNewContactForm && tipoParticipante === 'contacto' && (
                        <div className="border-2 border-dashed border-green-300 rounded-lg p-4 mb-4 bg-green-50">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-green-800 flex items-center gap-2">
                              <UserPlus className="w-4 h-4" /> Crear nuevo contacto
                            </h4>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setShowNewContactForm(false);
                                setNewContactoForm({ nombre: '', nif_cif: '', email: '', telefono: '', direccion: '', tipo: 'cliente' });
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                              <Label className="text-xs">Nombre *</Label>
                              <Input 
                                value={newContactoForm.nombre}
                                onChange={(e) => setNewContactoForm({...newContactoForm, nombre: e.target.value})}
                                placeholder="Nombre completo"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">NIF/CIF</Label>
                              <Input 
                                value={newContactoForm.nif_cif}
                                onChange={(e) => setNewContactoForm({...newContactoForm, nif_cif: e.target.value})}
                                placeholder="12345678A"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Teléfono</Label>
                              <Input 
                                value={newContactoForm.telefono}
                                onChange={(e) => setNewContactoForm({...newContactoForm, telefono: e.target.value})}
                                placeholder="600 000 000"
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Email</Label>
                              <Input 
                                type="email"
                                value={newContactoForm.email}
                                onChange={(e) => setNewContactoForm({...newContactoForm, email: e.target.value})}
                                placeholder="email@ejemplo.com"
                                className="mt-1"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Dirección</Label>
                              <Input 
                                value={newContactoForm.direccion}
                                onChange={(e) => setNewContactoForm({...newContactoForm, direccion: e.target.value})}
                                placeholder="Calle, número, ciudad..."
                                className="mt-1"
                              />
                            </div>
                          </div>

                          <div className="mt-4 p-2 bg-green-100 rounded text-xs text-green-800">
                            <strong>Nota:</strong> El contacto se creará y se añadirá automáticamente como <strong>{ROLES_PARTICIPANTE.find(r => r.value === selectedRol)?.label || 'participante'}</strong>
                          </div>

                          <Button 
                            type="button"
                            onClick={handleCreateContactoAndAddParticipante}
                            disabled={saving || !newContactoForm.nombre.trim() || !selectedRol}
                            className="w-full mt-3 bg-green-600 hover:bg-green-700"
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                            Crear contacto y añadir como participante
                          </Button>
                        </div>
                      )}

                      {/* Botón para mostrar formulario cuando hay resultados */}
                      {!showNewContactForm && tipoParticipante === 'contacto' && filteredContactos.length > 0 && (
                        <Button 
                          type="button"
                          variant="outline" 
                          size="sm"
                          className="w-full mb-4 border-dashed"
                          onClick={() => setShowNewContactForm(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" /> ¿No encuentras el contacto? Crear nuevo
                        </Button>
                      )}

                      <Button onClick={handleAddParticipante} disabled={!selectedContacto || saving} className="w-full bg-blue-600 hover:bg-blue-700">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        Añadir participante
                      </Button>
                    </div>

                    {/* Lista de participantes actuales */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Participantes actuales ({participantes.length})
                      </h3>
                      
                      {participantes.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                          <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                          <p className="text-gray-500">No hay participantes registrados</p>
                          <p className="text-sm text-gray-400 mt-1">Usa el buscador de arriba para añadir participantes</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                          {Object.entries(participantesPorRol).map(([rol, lista]) => (
                            <div key={rol} className="border rounded-lg p-3">
                              <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                                <User className="w-4 h-4 text-blue-600" />
                                {rol}
                                <Badge variant="outline" className="ml-2">{lista.length}</Badge>
                              </h4>
                              <div className="space-y-2">
                                {lista.map((p) => (
                                  <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${p.tipo_participante === 'organismo' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                                        {p.tipo_participante === 'organismo' ? <Building2 className="w-4 h-4 text-purple-600" /> : <User className="w-4 h-4 text-blue-600" />}
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-900 text-sm">{p.nombre_completo || [p.nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean).join(' ') || p.nombre || 'Sin nombre'}</p>
                                        <div className="flex gap-2 text-xs text-gray-500">
                                          {p.email && <span>✉️ {p.email}</span>}
                                          {p.telefono && <span>📞 {p.telefono}</span>}
                                        </div>
                                      </div>
                                    </div>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => handleRemoveParticipante(p.id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cerrar</Button>
                  </DialogFooter>
                </TabsContent>

                {/* TAB: ORGANISMOS */}
                <TabsContent value="organismos" className="mt-4">
                  <div className="space-y-6">
                    {/* Asignar Juzgado */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Building2 className="w-4 h-4" /> Asignar Juzgado
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Selecciona el juzgado desde la lista de organismos registrados en Contactos. 
                        Esta relación permitirá ver los expedientes asociados desde la ficha del organismo.
                      </p>
                      
                      {/* Buscador de organismos */}
                      <div className="mb-4">
                        <Label>Buscar organismo</Label>
                        <div className="relative mt-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input 
                            value={searchContacto}
                            onChange={(e) => setSearchContacto(e.target.value)}
                            placeholder="Buscar por nombre del organismo..."
                            className="pl-10"
                          />
                        </div>
                      </div>

                      {/* Lista de organismos */}
                      <div className="border rounded-lg max-h-[250px] overflow-y-auto bg-white mb-4">
                        {loadingContactos ? (
                          <div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
                        ) : filteredOrganismos.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <Building2 className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                            <p>No se encontraron organismos</p>
                            <p className="text-xs mt-1">Añade organismos en la sección de Contactos</p>
                          </div>
                        ) : (
                          <div className="divide-y">
                            {filteredOrganismos.map((org) => (
                              <div 
                                key={org.id}
                                onClick={() => {
                                  setEditForm({
                                    ...editForm, 
                                    juzgado_id: org.id,
                                    juzgado: org.nombre
                                  });
                                }}
                                className={`p-3 cursor-pointer hover:bg-purple-50 transition-colors ${editForm.juzgado_id === org.id ? 'bg-purple-100 border-l-4 border-purple-600' : ''}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-purple-600" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">{org.nombre}</p>
                                    <div className="flex gap-3 text-xs text-gray-500">
                                      {org.direccion && <span>📍 {org.direccion}</span>}
                                      {org.telefono && <span>📞 {org.telefono}</span>}
                                    </div>
                                  </div>
                                  {editForm.juzgado_id === org.id && <CheckCircle className="w-5 h-5 text-purple-600" />}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Juzgado seleccionado */}
                      {editForm.juzgado_id ? (
                        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-5 h-5 text-purple-600" />
                              <div>
                                <p className="font-medium text-purple-800">{editForm.juzgado}</p>
                                <p className="text-xs text-purple-600">Juzgado asignado</p>
                              </div>
                            </div>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm"
                              onClick={() => setEditForm({...editForm, juzgado_id: null, juzgado: ''})}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
                          <p className="text-sm text-yellow-700 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            No hay juzgado asignado. Selecciona uno de la lista.
                          </p>
                        </div>
                      )}

                      <Button 
                        onClick={async () => {
                          setSaving(true);
                          try {
                            // Only send valid backend fields
                            const validData = {
                              titulo: editForm.titulo,
                              expediente_judicial: editForm.expediente_judicial,
                              nig: editForm.nig,
                              referencia_extrajudicial: editForm.referencia_extrajudicial,
                              descripcion: editForm.descripcion,
                              tipo_procedimiento: editForm.tipo_procedimiento,
                              rama: editForm.rama,
                              fase: editForm.fase,
                              estado: editForm.estado,
                              juzgado: editForm.juzgado,
                              juzgado_id: editForm.juzgado_id,
                              procurador: editForm.procurador,
                              contrario: editForm.contrario,
                              cliente_id: editForm.cliente_id === '' ? null : editForm.cliente_id,
                              valor_economico: editForm.valor_economico === '' ? 0 : parseFloat(editForm.valor_economico) || 0,
                              fecha_inicio: editForm.fecha_inicio,
                              observaciones: editForm.observaciones
                            };
                            await expedientesAPI.update(id, validData);
                            toast({ title: "Expediente actualizado", description: "Los datos se han guardado correctamente" });
                            loadExpediente();
                          } catch (error) {
                            toast({ title: "Error", description: "No se pudo actualizar el expediente", variant: "destructive" });
                          } finally {
                            setSaving(false);
                          }
                        }} 
                        disabled={saving} 
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                        Guardar Juzgado
                      </Button>
                    </div>

                    {/* Información sobre la relación */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Información
                      </h4>
                      <p className="text-sm text-blue-700">
                        Al asignar un juzgado (organismo) a este expediente, podrás ver este expediente 
                        desde la ficha del organismo en la sección "Expedientes asociados como Juzgado".
                      </p>
                    </div>
                  </div>

                  <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cerrar</Button>
                  </DialogFooter>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>

          {/* DESCARGAR/IMPRIMIR */}
          <Button variant="outline" onClick={handleDownloadReport}>
            <Download className="w-4 h-4 mr-2" />DESCARGAR/IMPRIMIR
          </Button>
        </div>
      </div>

      {/* Acordeón de secciones */}
      <Accordion type="multiple" defaultValue={["datos", "actuaciones", "analisis-ia", "pagos", "participantes", "eventos", "adjuntos"]} className="space-y-4">
        
        {/* DATOS DEL EXPEDIENTE */}
        <AccordionItem value="datos" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">📋</div>
              Datos del expediente
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Información básica</h3>
                <div className="space-y-3 text-sm">
                  <div><span className="text-gray-500">Nº Expediente:</span><span className="ml-2 font-medium text-blue-700">{expediente.numero_expediente}</span></div>
                  <div><span className="text-gray-500">Expediente Judicial:</span><span className="ml-2 font-medium">{expediente.expediente_judicial || 'No especificado'}</span></div>
                  <div><span className="text-gray-500">NIG:</span><span className="ml-2 font-medium">{expediente.nig || 'No especificado'}</span></div>
                  {expediente.referencia_extrajudicial && (
                    <div><span className="text-gray-500">Nº Reclamación Extrajudicial:</span><span className="ml-2 font-medium text-orange-600">{expediente.referencia_extrajudicial}</span></div>
                  )}
                  <div><span className="text-gray-500">Estado:</span><Badge className="ml-2 bg-green-100 text-green-800">{expediente.estado}</Badge></div>
                  <div><span className="text-gray-500">Descripción:</span><p className="mt-1 text-gray-900">{expediente.descripcion}</p></div>
                  <div><span className="text-gray-500">Tipo:</span><span className="ml-2 font-medium">{expediente.tipo_procedimiento || expediente.tipo || 'No especificado'}</span></div>
                  <div><span className="text-gray-500">Rama:</span><span className="ml-2 font-medium">{expediente.rama || 'No especificada'}</span></div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Datos jurídicos y participantes clave</h3>
                <div className="space-y-3 text-sm">
                  {/* Cliente - desde participantes */}
                  <div className="flex items-start">
                    <span className="text-gray-500 min-w-[100px]">Cliente:</span>
                    {(() => {
                      const cliente = participantes.find(p => p.rol === 'cliente');
                      if (cliente) {
                        return (
                          <div className="ml-2">
                            <a href={`/contactos/${cliente.contacto_id}`} className="font-medium text-blue-600 hover:underline">
                              {cliente.nombre}
                            </a>
                            {cliente.telefono && <span className="text-gray-500 text-xs ml-2">📞 {cliente.telefono}</span>}
                            {cliente.email && <span className="text-gray-500 text-xs ml-2">✉️ {cliente.email}</span>}
                          </div>
                        );
                      }
                      return <span className="ml-2 text-gray-400 italic">No asignado</span>;
                    })()}
                  </div>
                  {/* Procurador propio - desde participantes */}
                  <div className="flex items-start">
                    <span className="text-gray-500 min-w-[100px]">Procurador:</span>
                    {(() => {
                      const procurador = participantes.find(p => p.rol === 'procurador_propio');
                      if (procurador) {
                        return (
                          <div className="ml-2">
                            <a href={`/contactos/${procurador.contacto_id}`} className="font-medium text-green-600 hover:underline">
                              {procurador.nombre}
                            </a>
                            {procurador.telefono && <span className="text-gray-500 text-xs ml-2">📞 {procurador.telefono}</span>}
                          </div>
                        );
                      }
                      // Fallback al campo texto si no hay participante
                      return <span className="ml-2 font-medium">{expediente.procurador || <span className="text-gray-400 italic">No asignado</span>}</span>;
                    })()}
                  </div>
                  <div><span className="text-gray-500">Fase:</span><span className="ml-2 font-medium">{expediente.fase || 'No especificada'}</span></div>
                  <div>
                    <span className="text-gray-500">Juzgado:</span>
                    {expediente.juzgado_id && expediente.juzgado_nombre ? (
                      <a href={`/contactos/${expediente.juzgado_id}`} className="ml-2 font-medium text-blue-600 hover:underline">
                        {expediente.juzgado_nombre}
                      </a>
                    ) : (
                      <span className="ml-2 text-gray-900">{expediente.juzgado || 'No especificado'}</span>
                    )}
                  </div>
                  {/* Contrario - desde participantes */}
                  <div className="flex items-start">
                    <span className="text-gray-500 min-w-[100px]">Contrario:</span>
                    {(() => {
                      const contrario = participantes.find(p => p.rol === 'parte_contraria');
                      if (contrario) {
                        return (
                          <a href={`/contactos/${contrario.contacto_id}`} className="ml-2 font-medium text-red-600 hover:underline">
                            {contrario.nombre}
                          </a>
                        );
                      }
                      // Fallback al campo texto si no hay participante
                      return <span className="ml-2 font-medium">{expediente.contrario || <span className="text-gray-400 italic">No especificado</span>}</span>;
                    })()}
                  </div>
                  <div><span className="text-gray-500">Fecha inicio:</span><span className="ml-2 font-medium">{expediente.fecha_inicio ? new Date(expediente.fecha_inicio).toLocaleDateString('es-ES') : 'No especificada'}</span></div>
                  <div><span className="text-gray-500">Valor económico:</span><span className="ml-2 font-medium">{expediente.valor_economico ? `${expediente.valor_economico.toLocaleString('es-ES')} €` : 'No especificado'}</span></div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ACTUACIONES */}
        <AccordionItem value="actuaciones" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white">📝</div>
              Actuaciones ({actuaciones.length})
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={actuacionesRef}>
              <ActuacionesSection
                expedienteId={parseInt(id)}
                actuaciones={actuaciones}
                onCreateActuacion={handleCreateActuacionNew}
                onRefresh={loadExpediente}
              />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ANÁLISIS IA */}
        <AccordionItem value="analisis-ia" className="bg-white border rounded-lg border-l-4 border-l-purple-500">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center text-white">✨</div>
              Análisis IA
              <Badge className="ml-2 bg-purple-100 text-purple-700">Nuevo</Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <AnalisisIAExpediente expediente={expediente} actuaciones={actuaciones} />
          </AccordionContent>
        </AccordionItem>

        {/* PAGOS */}
        <AccordionItem value="pagos" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white">💰</div>
              Pagos
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <PagosSection expedienteId={parseInt(id)} />
          </AccordionContent>
        </AccordionItem>

        {/* PARTICIPANTES */}
        <AccordionItem value="participantes" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">👥</div>
              Participantes ({participantes.length})
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">Personas y organismos relacionados con este expediente</p>
              <Button size="sm" onClick={() => { setIsEditDialogOpen(true); setEditTab('participantes'); }} className="bg-blue-700 hover:bg-blue-800">
                <Edit className="w-4 h-4 mr-2" />GESTIONAR PARTICIPANTES
              </Button>
            </div>

            {participantes.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No hay participantes registrados</p>
                <p className="text-sm text-gray-400 mt-1">Usa el botón "Editar" para añadir participantes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(participantesPorRol).map(([rol, lista]) => (
                  <div key={rol} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />{rol}<Badge variant="outline" className="ml-2">{lista.length}</Badge>
                    </h4>
                    <div className="space-y-2">
                      {lista.map((p) => (
                        <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${p.tipo_participante === 'organismo' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                              {p.tipo_participante === 'organismo' ? <Building2 className="w-4 h-4 text-purple-600" /> : <User className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{p.nombre_completo || [p.nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean).join(' ') || p.nombre || 'Sin nombre'}</p>
                              <div className="flex gap-3 text-xs text-gray-500">
                                {p.email && <span>✉️ {p.email}</span>}
                                {p.telefono && <span>📞 {p.telefono}</span>}
                              </div>
                            </div>
                          </div>
                          {/* Botón dar acceso al portal para clientes */}
                          {p.rol === 'cliente' && p.contacto_id && p.email && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal-cliente/registrar-con-envio`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      contacto_id: p.contacto_id,
                                      email: p.email,
                                      expedientes_ids: [parseInt(id)]
                                    })
                                  });
                                  const data = await response.json();
                                  if (response.ok && data.success) {
                                    toast({
                                      title: 'Acceso al portal creado',
                                      description: data.email_enviado 
                                        ? `Código enviado a ${p.email}` 
                                        : `Código: ${data.codigo_acceso}`
                                    });
                                  } else {
                                    throw new Error(data.detail || 'Error');
                                  }
                                } catch (err) {
                                  toast({ title: 'Error', description: err.message, variant: 'destructive' });
                                }
                              }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Dar acceso al portal del cliente"
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* TAREAS */}
        <AccordionItem value="tareas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">✓</div>
              Tareas
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={tareasRef}>
              <TareasSection expedienteId={parseInt(id)} tareas={tareas} onRefresh={loadExpediente} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* NOTAS */}
        <AccordionItem value="notas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">📄</div>
              Notas
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={notasRef}>
              <NotasSection expedienteId={parseInt(id)} notas={notas} onRefresh={loadExpediente} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* EVENTOS */}
        <AccordionItem value="eventos" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white">📅</div>
              Eventos
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <div ref={eventosRef}>
              <EventosSection expedienteId={parseInt(id)} onRefresh={loadExpediente} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ADJUNTOS */}
        <AccordionItem value="adjuntos" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">📎</div>
              Adjuntos
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <AdjuntosSection 
              entityType="expediente" 
              entityId={parseInt(id)} 
              showHeader={false}
            />
          </AccordionContent>
        </AccordionItem>

        {/* FACTURAS */}
        <AccordionItem value="facturas" className="bg-white border rounded-lg">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white">💶</div>
              Facturas ({facturas.length})
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            {facturas.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No hay facturas asociadas</p>
            ) : (
              <div className="space-y-3">
                {facturas.map((factura) => (
                  <Card key={factura.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{factura.numero}</h4>
                          <p className="text-sm text-gray-600 mt-1">{factura.concepto}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">{factura.total?.toFixed(2)}€</p>
                          <Badge className={factura.estado === 'Pagada' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>{factura.estado}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Floating Action Button */}
      <FloatingActionButton onAction={handleFABAction} />
    </div>
  );
};

export default ExpedienteDetalle;
